import React, { JSX } from 'react';
import { useMemo, useEffect, useState } from 'react';
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';

import './DataTable.css';
import { getArticles } from '../api/articleApi';
import { Article, Story } from '../types';

import icon_delete from '../assets/actions/icon_delete.png';
import icon_edit from '../assets/actions/icon_edit.png';
import { getStories } from '../api/storyApi';

const DataTable = ({
  columnDefinitions,
  filters,
  refresh,
  onShowArticle,
  dataSource,
}: {
  columnDefinitions: Array<{ id: string; label: string; type?: string; accessorFn?: (row: any) => any; cell?: (info: any) => JSX.Element; align?: string; linkTo?: string; actions?: Array<{ type: string; onClick?: (row: any) => void }> }>;
  filters: any;
  refresh: boolean;
  onShowArticle: (item: Article | Story) => void;
  dataSource: string;
}) => {

  const [data, setData] = useState<Article[] | Story[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState({ id: 'id', desc: true });
  const [selectedRows, setSelectedRows] = useState({});
  const [expandedRoles, setExpandedRoles] = useState({});


  const handleSort = (columnId: string) => {
    if (columnId === 'photos') {
      return;
    }
    setSortBy((prevSortBy) => {
      if (prevSortBy.id === columnId) {
        return { id: columnId, desc: !prevSortBy.desc };
      }
      return { id: columnId, desc: false };
    });
  };

  const refreshData = () => {
    setIsLoading(true);
    if (dataSource === 'articles') {
      getArticles(currentPage, { id: 'created_at', desc: true }, { 'deleted_at': null }).then((articleList) => {
        setData(articleList.data);
        setTotalPages(articleList.pagination.total_pages);
      }).finally(() => {
        setIsLoading(false);
      });
    } else if (dataSource === 'stories') {
      getStories(currentPage, { id: 'created_at', desc: true }, { 'deleted_at': null }).then((articleList) => {
        setData(articleList.data);
        setTotalPages(articleList.pagination.total_pages);
      }).finally(() => {
        setIsLoading(false);
      });
    }
  };

  useEffect(() => {
    refreshData();
  }, [currentPage, refresh]);

  const generateColumns = (columns: Column[]) => {
    return columns.map((col) => {
      if (col.type === 'actions') {
        return {
          id: 'actions',
          header: () => <span></span>,
          cell: ({ row }: { row: any }) => (
            <div className="image-container align-center">
              {col.actions?.map((action) => {
                let icon;
                switch (action.type) {
                  case 'edit':
                    icon = icon_edit;
                    break;
                  case 'delete':
                    icon = icon_delete;
                    break;
                  default:
                    return null;
                }
                return (
                  <img
                    key={action.type}
                    src={icon}
                    alt={action.type}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (action.onClick) {
                        action.onClick(row.original);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                );
              })}
            </div>
          ),
        };
      } else if (col.type === 'export') {
        return {
          id: 'export',
          header: () => <span>exportar</span>,
          cell: () => (
            <div className="image-container align-center">
              <img src={''} alt="export" />
            </div>
          ),
        };
      } else {
        return {
          accessorFn: col.accessorFn
            ? col.accessorFn
            : (row: any) => {
              return row[col.id] !== undefined && row[col.id] !== null ? row[col.id] : '-';
            },
          id: col.id,
          header: () => (
            <span onClick={() => handleSort(col.id)}>
              {col.label}{' '}
              {sortBy.id === col.id ? (sortBy.desc ? '↓' : '↑') : ''}
            </span>
          ),
          cell: col.cell
            ? col.cell
            : (info: { row: { original: { [x: string]: any; }; }; getValue: () => string | number | bigint | boolean | React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | Promise<string | number | bigint | boolean | React.ReactPortal | React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined> | null | undefined; }) => {
              const value = info.getValue();
              const displayValue = col.id === 'title' && typeof value === 'string' 
                ? truncateText(value) 
                : value;
              
              return (
                <div className={`align-${col.align}`} title={col.id === 'title' && typeof value === 'string' ? value : undefined}>
                  {col.linkTo ? (
                    <a href={`/anomalias/${info.row.original[col.linkTo]}`}>
                      {displayValue}
                    </a>
                  ) : (
                    displayValue
                  )}
                </div>
              );
            },
        };
      }
    });
  };
  interface Column {
    id: string;
    label: string;
    align?: string;
    type?: string;
    accessorFn?: (row: any) => any;
    cell?: (info: any) => JSX.Element;
    linkTo?: string;
    actions?: Array<{ type: string; onClick?: (row: any) => void }>;
  }

  const columns = useMemo(
    () => [...generateColumns(columnDefinitions)],
    [sortBy, expandedRoles, columnDefinitions]
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0 } },
  });

  const truncateText = (text: string, maxWords: number = 10): string => {
    if (typeof text !== 'string') return text;
    
    const isMobile = window.innerWidth <= 767;
    if (!isMobile) return text;
    
    const words = text.split(' ');
    if (words.length <= maxWords) return text;
    
    return words.slice(0, maxWords).join(' ') + '...';
  };

  const handleRowClick = (row: Article | Story) => {
    if (onShowArticle) {
      onShowArticle(row);
    }
  };

  if (isLoading) {
    return (
      <div className="table-loading">
        <div className="dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div className="loading-text">Cargando datos...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  return (
    <>
      <table key={Object.keys(expandedRoles).length}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} colSpan={header.colSpan}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => handleRowClick(row.original)}
              style={{ cursor: 'pointer' }}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pagination">
        <div className="pagination-container">
          <button
            className="pagination-button"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            {'<<'}
          </button>
          <button
            className="pagination-button"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {'<'}
          </button>

          <span className="pagination-info">
            Página{' '}
            <strong>
              {totalPages === 0 ? 0 : currentPage} de {totalPages}
            </strong>{' '}
          </span>

          <button
            className="pagination-button"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            {'>'}
          </button>
          <button
            className="pagination-button"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            {'>>'}
          </button>
          <span className="pagination-go-to">
            | Ir a página:{' '}
            <input
              type="number"
              defaultValue={currentPage}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) : 1;
                if (page >= 1 && page <= totalPages) {
                  setCurrentPage(page);
                }
              }}
              style={{ width: '100px' }}
            />
          </span>
        </div>
      </div>
    </>
  );
};

export default DataTable;
